/**
 * The four analysis panels: trial conversion, unpaid ageing, add-on attach
 * rate and promo-code performance.
 *
 * Two to a row, each a fixed-height card: a headline figure that does not
 * move, over a list that scrolls inside the card. That keeps the row heights
 * equal however much data one of them happens to hold, so the grid never has
 * one card three times the height of its neighbour.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../store/AppContext.jsx';
import Icon from '../lib/icons.jsx';
import { Card, CardHead } from './Primitives.jsx';
import { moduleStats, modulePrice } from '../domain/pricing.js';

/** Conversion rates read green when healthy, red when they are not. */
const rateTone = rate => (rate >= 50 ? 'ok' : rate < 25 ? 'bad' : 'warn');

const Sub = ({ children }) => <div className="inslab">{children}</div>;

/** A percentage as a chip rather than coloured text, so it reads as a value. */
const Rate = ({ value }) => <span className={`rchip ${rateTone(value)}`}>{value}%</span>;

/** Conversion split by one column of the client record. */
function SegmentTable({ title, entries }) {
  if (!entries.length) return null;
  return (
    <>
      <Sub>{title}</Sub>
      <table className="mini">
        <tbody>
          {entries.map(([name, v]) => (
            <tr key={name}>
              <td>{name}</td>
              <td className="r">{v.w}/{v.n}</td>
              <td className="r"><Rate value={Math.round(v.w / v.n * 100)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

export default function InsightCards() {
  const { S, rows, derive, fmtMoney, setFlag } = useApp();
  const navigate = useNavigate();

  /* One walk of the book per change, not per render. */
  const pool = useMemo(() => rows.map(r => ({ r, d: derive(r) })).filter(x => !x.d.excluded), [rows, derive]);
  const finished = pool.filter(x => x.d.trial && x.d.dT < 0);
  const converted = finished.filter(x => x.d.paid);
  const rate = finished.length ? Math.round(converted.length / finished.length * 100) : null;

  /** Group finished trials by a column and count how many converted. */
  const segment = key => {
    const groups = {};
    pool.forEach(x => {
      if (!(x.d.trial && x.d.dT < 0)) return;
      const v = String(x.r[key] || '').trim() || 'Unassigned';
      groups[v] = groups[v] || { n: 0, w: 0 };
      groups[v].n += 1;
      if (x.d.paid) groups[v].w += 1;
    });
    return Object.entries(groups).sort((a, b) => b[1].n - a[1].n).slice(0, 8);
  };

  const unpaid = pool.filter(x => x.d.unpaidDays !== null);
  const buckets = [
    [`0–${S.ageMid} days`, unpaid.filter(x => x.d.unpaidDays <= S.ageMid).length, 'age1', 'ok'],
    [`${S.ageMid + 1}–${S.ageLate} days`,
      unpaid.filter(x => x.d.unpaidDays > S.ageMid && x.d.unpaidDays <= S.ageLate).length, 'age2', 'warn'],
    [`Over ${S.ageLate} days`, unpaid.filter(x => x.d.unpaidDays > S.ageLate).length, 'age3', 'bad']
  ];
  const maxBucket = Math.max(1, ...buckets.map(x => x[1]));

  const promos = {};
  pool.forEach(x => {
    const p = String(x.r.promo || '').trim();
    if (!p || p === '-') return;
    promos[p] = promos[p] || { n: 0, w: 0 };
    promos[p].n += 1;
    if (x.d.paid) promos[p].w += 1;
  });
  /* The list scrolls now, so it can afford to show more than a handful - but
     the headline must count every code, not just the ones on screen. */
  const promoAll = Object.entries(promos).sort((a, b) => b[1].n - a[1].n);
  const promoRows = promoAll.slice(0, 24);
  const promoUses = Object.values(promos).reduce((t, v) => t + v.n, 0);

  const mods = moduleStats(S, rows, derive);

  /* `keepFilter` or the page change would clear the band that was clicked. */
  const showAgeing = f => {
    setFlag(f);
    navigate('/payments', { state: { keepFilter: true } });
    window.scrollTo(0, 0);
  };

  return (
    <div className="insights">
      {/* -------------------------------------------------- trial to paid */}
      <Card className="inscard" data-tone="g-green">
        <CardHead icon="chart" tone="g-green" title="Trial → paid" />
        <div className="inshero">
          <div className="insbig">{rate === null ? '—' : `${rate}%`}</div>
          <div className="insnote">
            <b>{converted.length}</b> converted of <b>{finished.length}</b> finished trials
          </div>
          <div className="insbar"><i style={{ width: `${rate || 0}%` }} /></div>
        </div>
        <div className="inscroll">
          {finished.length ? (
            <>
              <SegmentTable title="By source" entries={segment('source')} />
              <SegmentTable title="By owner" entries={segment('handler')} />
            </>
          ) : <div className="insempty">No trials have finished yet.</div>}
        </div>
      </Card>

      {/* ------------------------------------------------- unpaid ageing */}
      <Card className="inscard" data-tone="g-orange">
        <CardHead icon="card" tone="g-orange" title="Unpaid by age" />
        <div className="inshero">
          <div className="insbig">{unpaid.length}</div>
          <div className="insnote">
            {unpaid.length === 1 ? 'account' : 'accounts'} with money outstanding
          </div>
        </div>
        <div className="inscroll">
          {buckets.map(([label, value, f, tone]) => (
            <button type="button" className={`agerow ${tone}`} key={f} onClick={() => showAgeing(f)}>
              <span className="agelab">{label}</span>
              <span className="agenum">{value}</span>
              <span className="agetrack"><i style={{ width: `${value / maxBucket * 100}%` }} /></span>
            </button>
          ))}
          <div className="insnote pad">
            {buckets[2][1]
              ? <><b className="hot">{buckets[2][1]}</b> more than {S.ageLate} days overdue.</>
              : `Nothing more than ${S.ageLate} days overdue.`}
          </div>
        </div>
      </Card>

      {/* --------------------------------------------------- add-on rate */}
      <Card className="inscard" data-tone="g-cyan">
        <CardHead icon="star" tone="g-cyan" title="Add-on modules" />
        <div className="inshero">
          <div className="insbig">{mods.rate === null ? '—' : `${mods.rate}%`}</div>
          <div className="insnote">
            <b>{mods.attached}</b> of <b>{mods.eligible}</b> eligible accounts have a module
            {mods.bundled ? <> · <b>{mods.bundled}</b> bundled</> : null}
          </div>
          <div className="insrev">
            <Icon name="zap" />Add-on revenue <b>{fmtMoney(mods.rev)}/mo</b>
          </div>
        </div>
        <div className="inscroll">
          {mods.top.length ? (
            <table className="mini">
              <thead>
                <tr><th>Module</th><th className="r">Clients</th><th className="r">Monthly</th></tr>
              </thead>
              <tbody>
                {mods.top.map(([name, count]) => (
                  <tr key={name}>
                    <td>{name.replace('XYRA ', '')}</td>
                    <td className="r">{count}</td>
                    <td className="r">{fmtMoney(count * modulePrice(S, name))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="insempty">Nobody has added a module yet.</div>}
        </div>
      </Card>

      {/* -------------------------------------------------- promo codes */}
      <Card className="inscard" data-tone="g-pink">
        <CardHead icon="tag" tone="g-pink" title="Promo codes" />
        <div className="inshero">
          <div className="insbig">{promoAll.length || '—'}</div>
          <div className="insnote">
            {promoAll.length
              ? (
                <>
                  <b>{promoUses}</b> {promoUses === 1 ? 'use' : 'uses'} across{' '}
                  <b>{promoAll.length}</b> {promoAll.length === 1 ? 'code' : 'codes'}
                  {promoAll.length > promoRows.length
                    ? <> · top {promoRows.length} listed</>
                    : null}
                </>
              )
              : 'nothing recorded yet'}
          </div>
        </div>
        <div className="inscroll">
          {promoRows.length ? (
            <table className="mini">
              <thead>
                <tr><th>Code</th><th className="r">Used</th><th className="r">Paid</th><th className="r">Rate</th></tr>
              </thead>
              <tbody>
                {promoRows.map(([code, v]) => (
                  <tr key={code}>
                    <td><b>{code}</b></td>
                    <td className="r">{v.n}</td>
                    <td className="r">{v.w}</td>
                    <td className="r"><Rate value={Math.round(v.w / v.n * 100)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="insempty">No promo codes recorded yet.</div>}
        </div>
      </Card>
    </div>
  );
}
